import { database } from './core';
import { 
  ref, 
  query, 
  orderByChild, 
  limitToLast, 
  get, 
  onValue, 
  off, 
  endAt
} from 'firebase/database';
import { updateData } from './api-wrapper.service';
import { getCurrentUser } from './auth.service';
import { subscriptionManager } from './subscription-manager';
import logger from '../../utils/logger';

// Redux store reference for real-time updates
let reduxStore: any = null;

/**
 * Set Redux store reference for real-time updates
 * This should be called from the store configuration
 */
export const setReduxStore = (store: any) => {
  reduxStore = store;
};

/**
 * Notify Redux of Firebase feed data updates for like state synchronization
 * This ensures that real-time Firebase updates are reflected in Redux state
 */
const notifyReduxOfFeedUpdates = (feedItemsData: Record<string, any>, userId: string) => {
  if (!reduxStore || !feedItemsData) return;
  
  try {
    // Import the action dynamically to avoid circular dependency
    import('../../store/slices/feedSlice').then(({ handleFirebaseDataUpdate }) => {
      // Process each feed item that has likes data
      Object.keys(feedItemsData).forEach(postId => {
        const feedItem = feedItemsData[postId];
        if (feedItem && (feedItem.likes || feedItem.likesCount !== undefined || (feedItem as any).likeCount !== undefined)) {
          // Dispatch update to Redux
          reduxStore.dispatch(handleFirebaseDataUpdate({
            postId,
            firebaseData: feedItem,
            currentUserId: userId
          }));
        }
      });
    }).catch(error => {
      logger.error('Error importing feedSlice action:', error);
    });
  } catch (error) {
    logger.error('Error notifying Redux of feed updates:', error);
  }
};

// FeedItem interface matching original app EXACTLY
export interface FeedItem {
  // Core fields from original app
  owner: string;
  dateCreated: number;
  date: number; // Can be same as dateCreated, stories don't have separate date
  postType: 'training' | 'competition' | 'beltPromotion' | 'exercise' | 'story' | 'workoutSession';
  postSubType?: string; // For trainings (type) and exercises (type)
  
  // These are NOT in feed items in original app - they're on the actual posts
  // Keeping for backward compatibility but should not be used
  id?: string;
  itemRef?: string;
  likeCount?: number;
  commentCount?: number;
  shownOnFeeds?: boolean;
}

// Define batch size
const BATCH_SIZE = 10;

const calculateAspectRatioFromBlob = async (blob: Blob): Promise<number> => {
  if (typeof window === 'undefined' || !window.URL?.createObjectURL) {
    return 1;
  }

  return new Promise<number>((resolve) => {
    try {
      const objectUrl = window.URL.createObjectURL(blob);
      const image = new Image();

      image.onload = () => {
        const width = image.naturalWidth || image.width;
        const height = image.naturalHeight || image.height || 1;
        window.URL.revokeObjectURL(objectUrl);
        if (!height) {
          resolve(1);
          return;
        }
        resolve(width / height);
      };

      image.onerror = () => {
        window.URL.revokeObjectURL(objectUrl);
        resolve(1);
      };

      image.src = objectUrl;
    } catch (error) {
      logger.warn('Failed to calculate image aspect ratio', error);
      resolve(1);
    }
  });
};

/**
 * Gets user feed items - matches original app pattern
 * @param uid User ID
 * @param wantedItemsCount Number of items to fetch
 * @param callback Callback with (feedItemsList, feedItemsData)
 * @returns Unsubscribe function
 */
export const getUserFeedItems = (
  uid: string,
  wantedItemsCount: number,
  callback: (feedItemsList: string[], feedItemsData: Record<string, any>) => void
): (() => void) => {
  if (!uid) {
    logger.info('[feed.service] No user ID provided');
    callback([], {});
    return () => {};
  }

  logger.info(`[feed.service] Getting feed items for user: ${uid}, count: ${wantedItemsCount}`);
  
  // Create reference to user's feed - EXACT path from original app
  const feedRef = ref(database, `feeds/${uid}`);
  const feedQuery = query(
    feedRef,
    orderByChild('dateCreated'),
    limitToLast(wantedItemsCount)
  );

  // Remove any existing listeners
  off(feedQuery);

  // Track if we've received an initial response
  let hasReceivedInitialResponse = false;

  // Set up real-time listener matching original app
  // Capture the unsubscribe function returned by onValue for proper cleanup
  const unsubscribe = onValue(feedQuery, 
    (snapshot) => {
      hasReceivedInitialResponse = true;
      const feedItemsList: string[] = [];
      
      if (!snapshot.exists()) {
        logger.info('[feed.service] No feed data found - feed may be empty or still loading');
        // Still callback with empty data so UI can show empty state
        callback([], {});
        return;
      }

      logger.info(`[feed.service] Feed data found, processing ${snapshot.size} items`);
      
      // IMPORTANT: Keep query order using Firebase's forEach
      snapshot.forEach((itemSnap) => {
        if (itemSnap.key) {
          feedItemsList.push(itemSnap.key);
        }
      });

      // Reverse to get newest first
      feedItemsList.reverse();

      logger.info(`[feed.service] Returning ${feedItemsList.length} feed items`);
      callback(feedItemsList, snapshot.val());
      
      // Dispatch real-time updates to Redux for like state synchronization
      notifyReduxOfFeedUpdates(snapshot.val(), uid);
    },
    (error) => {
      // Check if this is a permission error that might resolve after auth completes
      if ((error as any).code === 'PERMISSION_DENIED' && !hasReceivedInitialResponse) {
        logger.warn('[feed.service] Permission denied on initial load - auth may still be establishing');
        // Wait a bit and retry once
        setTimeout(() => {
          logger.info('[feed.service] Retrying after permission denied...');
          // Try one more time
          get(feedQuery).then((snapshot) => {
            hasReceivedInitialResponse = true;
            const feedItemsList: string[] = [];
            
            if (!snapshot.exists()) {
              callback([], {});
              return;
            }
            
            snapshot.forEach((itemSnap) => {
              if (itemSnap.key) {
                feedItemsList.push(itemSnap.key);
              }
            });
            
            feedItemsList.reverse();
            callback(feedItemsList, snapshot.val());
          }).catch((retryError) => {
            logger.error('[feed.service] Retry failed:', retryError);
            callback([], {});
          });
        }, 1000);
        return;
      }
      
      hasReceivedInitialResponse = true;
      logger.error('[feed.service] Error retrieving feed items:', error);
      // Still callback so UI doesn't hang
      callback([], {});
    }
  );

  // Register with subscription manager for global cleanup
  const subscriptionKey = `feed-items:${uid}`;
  subscriptionManager.add(subscriptionKey, unsubscribe);

  // Return unsubscribe function that also removes from manager
  return () => {
    subscriptionManager.remove(subscriptionKey);
  };
};

/**
 * Gets next batch of feed items for pagination using a ONE-SHOT read.
 * Pagination batches don't need realtime updates - the previous onValue
 * subscription here was never unsubscribed, so every "Load More" leaked a
 * permanent listener until logout.
 * @param uid User ID
 * @param lastFeedItemDate dateCreated of oldest item currently loaded
 * @param callback Callback function
 */
export const getNextFeedBatch = (
  uid: string,
  lastFeedItemDate: number,
  callback: (feedItemsList: string[], feedItemsData: Record<string, any>) => void
): void => {
  const feedRef = ref(database, `feeds/${uid}`);
  const feedQuery = query(
    feedRef,
    orderByChild('dateCreated'),
    limitToLast(BATCH_SIZE),
    endAt(lastFeedItemDate - 1) // Don't include the last item
  );

  get(feedQuery)
    .then((snapshot) => {
      const feedItemsList: string[] = [];

      if (!snapshot.exists()) {
        callback([], {});
        return;
      }

      snapshot.forEach((itemSnap) => {
        if (itemSnap.key) {
          feedItemsList.push(itemSnap.key);
        }
      });

      feedItemsList.reverse();
      callback(feedItemsList, snapshot.val());
    })
    .catch((error) => {
      logger.error('[feed.service] Error fetching next feed batch:', error);
      // Still callback so UI doesn't hang
      callback([], {});
    });
};

/**
 * Gets a post from its database path - with improved error handling for Firebase permissions
 * @param dbPath Database path to the post
 * @param callback Callback with post data
 * @returns Unsubscribe function
 */
export const getPost = (
  dbPath: string,
  callback: (post: any) => void
): (() => void) => {
  const postRef = ref(database, dbPath);

  // Capture the unsubscribe function returned by onValue for proper cleanup
  const unsubscribe = onValue(
    postRef,
    (snapshot) => {
      if (snapshot.exists()) {
        const postData = { ...snapshot.val(), key: snapshot.key };
        callback(postData);
        
        // Notify Redux of real-time post updates for like synchronization
        if (reduxStore && postData.key) {
          try {
            const currentUser = getCurrentUser();
            if (currentUser && (postData.likes || postData.likesCount !== undefined || (postData as any).likeCount !== undefined)) {
              import('../../store/slices/feedSlice').then(({ handleFirebaseDataUpdate }) => {
                reduxStore.dispatch(handleFirebaseDataUpdate({
                  postId: postData.key,
                  firebaseData: postData,
                  currentUserId: currentUser.uid
                }));
              }).catch(error => {
                logger.error('Error importing feedSlice action for post update:', error);
              });
            }
          } catch (error) {
            logger.error('Error notifying Redux of post update:', error);
          }
        }
      } else {
        logger.info('Post does not exist at:', dbPath);
        // Return minimal data with key - matches original
        const key = getPostKey(dbPath);
        callback({ key });
      }
    },
    (error) => {
      logger.error('Error retrieving post at', dbPath, 'from db:', error);
      const key = getPostKey(dbPath);
      callback({ key });
    }
  );

  // Register with subscription manager for global cleanup
  const subscriptionKey = `feed-post:${dbPath}`;
  subscriptionManager.add(subscriptionKey, unsubscribe);

  // Return unsubscribe function that also removes from manager
  return () => {
    subscriptionManager.remove(subscriptionKey);
  };
};

/**
 * Gets a post from its database path using a one-time read (no persistent listener).
 * Use this for batch-fetching feed posts where real-time updates are not needed.
 * @param dbPath Database path to the post
 * @returns Post data with key, or minimal object with key if not found
 */
export const getPostOnce = async (dbPath: string): Promise<any> => {
  const postRef = ref(database, dbPath);
  try {
    const snapshot = await get(postRef);
    if (snapshot.exists()) {
      return { ...snapshot.val(), key: snapshot.key };
    }
    return { key: getPostKey(dbPath) };
  } catch (error) {
    logger.error('Error retrieving post at', dbPath, ':', error);
    return { key: getPostKey(dbPath) };
  }
};

/**
 * Gets the key of a post from its path - matches original app
 */
function getPostKey(dbPath: string): string {
  return dbPath.slice(dbPath.lastIndexOf('/') + 1) || 
         Math.random().toString(36).substr(2, 10); // Fallback for empty paths
}

/**
 * Fetches posts for given feed items - matches original app pattern
 * @param unfetchedKeysList Array of post keys to fetch
 * @param feedItemsData Feed items data containing post metadata
 * @param callback Callback when all posts are fetched
 */
export const fetchPosts = (
  unfetchedKeysList: string[],
  feedItemsData: Record<string, FeedItem>,
  callback: (posts: Record<string, any>) => void
): void => {
  const posts: Record<string, any> = {};
  let fetchedCount = 0;

  function savePosts(postData: any, owner: string = '') {
    posts[postData.key] = owner ? { ...postData, owner } : postData;
    fetchedCount++;
    
    if (fetchedCount === unfetchedKeysList.length) {
      // All posts fetched
      callback(posts);
    }
  }

  unfetchedKeysList.forEach(key => {
    const feedItem = feedItemsData[key];
    if (!feedItem) {
      // Handle missing feed item
      posts[key] = null;
      fetchedCount++;
      if (fetchedCount === unfetchedKeysList.length) callback(posts);
      return;
    }

    switch (feedItem.postType) {
      case 'training':
        getPost(`trainings/${key}`, savePosts);
        break;

      case 'competition':
        getPost(`competitions/${key}`, savePosts);
        break;

      case 'beltPromotion':
        // Belt Promotions don't have an 'owner' prop at root
        getPost(
          `beltPromotions/${feedItem.owner}/${key}`,
          postData => savePosts(postData, feedItem.owner)
        );
        break;

      case 'exercise':
        // Exercises don't have an 'owner' prop at root
        getPost(
          `exercises/${feedItem.owner}/${key}`,
          postData => savePosts(postData, feedItem.owner)
        );
        break;

      case 'story':
        // Stories don't have an 'owner' prop at root
        getPost(
          `stories/${feedItem.owner}/${key}`,
          postData => savePosts(postData, feedItem.owner)
        );
        break;

      case 'workoutSession':
        // Workout Sessions don't have an 'owner' prop at root
        getPost(
          `workoutSessions/${feedItem.owner}/${key}`,
          postData => savePosts(postData, feedItem.owner)
        );
        break;

      default:
        // Unknown type - mark as handled
        logger.warn(
          'fetchPosts - Unknown feed item type.',
          "Must be one of: 'training', 'competition', 'beltPromotion', 'exercise', 'story' or 'workoutSession'"
        );
        posts[key] = null;
        fetchedCount++;
        if (fetchedCount === unfetchedKeysList.length) callback(posts);
        break;
    }
  });

  // Handle empty list
  if (unfetchedKeysList.length === 0) {
    callback(posts);
  }
};

/**
 * Creates a training session and adds to feed - matching original app pattern
 * The cloud function will create the feed item automatically
 * @param trainingData Training data
 * @returns Training ID
 */
export const createTrainingPost = async (trainingData: any): Promise<string> => {
  const user = getCurrentUser();
  if (!user) throw new Error('Must be logged in');

  // Import the training service to use the proper addTraining function
  const { addTraining } = await import('./training.service');
  
  const training = {
    ...trainingData,
    owner: user.uid,
    shownOnFeed: true,
    shownOnFeeds: true, // This flag tells cloud function to add to feeds
    // Ensure type field for postSubType in feed
    type: trainingData.type || 'training'
  };

  // Use the training service which properly uses createWithKey (push)
  const createdTraining = await addTraining(training);
  
  logger.info('[feed.service] Training created, cloud function will update feeds');
  return createdTraining.id;
};

/**
 * Creates a competition and adds to feed
 * @param competitionData Competition data
 * @returns Competition ID
 */
export const createCompetitionPost = async (competitionData: any): Promise<string> => {
  const user = getCurrentUser();
  if (!user) throw new Error('Must be logged in');

  // Import the competition service to use the proper function
  const { createCompetition } = await import('./competition.service');
  
  const competition = {
    ...competitionData,
    owner: user.uid,
    shownOnFeed: true,
    shownOnFeeds: true
  };

  // Use the competition service which properly handles creation
  const competitionId = await createCompetition(competition);
  
  logger.info('[feed.service] Competition created, cloud function will update feeds');
  return competitionId;
};

/**
 * Creates a belt promotion and adds to feed - matching original app pattern
 * @param promotionData Promotion data
 * @returns Promotion ID
 */
export const createBeltPromotionPost = async (promotionData: any): Promise<string> => {
  const user = getCurrentUser();
  if (!user) throw new Error('Must be logged in');

  // For belt promotions, we'll use createWithKey from api-wrapper since the belt service uses setData
  const { createWithKey } = await import('./api-wrapper.service');
  
  const promotion = {
    ...promotionData,
    dateCreated: Date.now(),
    date: promotionData.date || Date.now(), // Date of promotion
    shownOnFeed: true,
    shownOnFeeds: true
  };

  // Use createWithKey which properly uses push()
  const promotionId = await createWithKey(`beltPromotions/${user.uid}`, promotion);
  
  logger.info('[feed.service] Belt promotion created, cloud function will update feeds');
  return promotionId;
};

/**
 * Gets the database path for a post based on its type and owner
 * Matches original app's storage structure
 */
export const getPostPath = (postType: string, postId: string, owner: string): string => {
  switch (postType) {
    case 'training':
    case 'competition':
      // These are stored globally with owner field
      return `${postType}s/${postId}`;
      
    case 'beltPromotion':
    case 'exercise':
    case 'story':
    case 'workoutSession':
      // These are stored under user path
      return `${postType}s/${owner}/${postId}`;
      
    default:
      logger.warn('Unknown post type:', postType);
      return '';
  }
};

/**
 * Generic create post function that determines post type from data
 * This is for backward compatibility with components expecting createPost
 * MATCHES ORIGINAL APP FLOW EXACTLY for backwards compatibility
 */
export const createPost = async (postData: { content: string; isPublic?: boolean; type?: string; media?: any[] }): Promise<string> => {
  const user = getCurrentUser();
  if (!user) throw new Error('Must be logged in');

  // STEP 1: Create story WITHOUT photos first (like original app)
  const storyDataWithoutPhotos = {
    story: postData.content, // Will be mapped to message.content in createStoryPost
    title: '', // Empty title for now
    location: null, // No location support in CreatePostModal yet
    taggedFriends: {}, // No friend tagging in CreatePostModal yet
    style: 'standard', // Default style
    backgroundColor: '', // No color support in CreatePostModal yet
    textAlign: 'center', // Default alignment
    photos: [], // Start with empty photos array - CRITICAL: this creates stories/{uid}/{storyKey}/photos as empty object
    public: postData.isPublic !== false // Public flag (not used in story structure but passed through)
  };
  
  const storyId = await createStoryPost(storyDataWithoutPhotos);
  
  // STEP 2: Upload photos using ORIGINAL APP PATTERN if any photos exist
  if (postData.media && postData.media.length > 0) {
    try {
      await uploadPhotosOriginalPattern(postData.media, storyId, user.uid);
      logger.info(`[feed.service] Story ${storyId} photos uploaded using original app pattern`);
    } catch (error) {
      logger.error('Error uploading photos for story:', error);
      // Story still created successfully even if photos failed
    }
  }
  
  return storyId;
};

/**
 * Uploads photos using the ORIGINAL APP PATTERN for backwards compatibility
 * This matches the MediaGallery component's onChangesConfirmed flow exactly
 * @param media Media array from CreatePostModal
 * @param storyId Story ID for paths
 * @param userId User ID for paths
 */
async function uploadPhotosOriginalPattern(media: any[], storyId: string, userId: string): Promise<void> {
  const { updateData } = await import('./api-wrapper.service');
  const { uploadToStorage } = await import('./storage.service');

  // Filter to photos only
  const photos = media.filter(item => item.type === 'photo' && item.url);

  if (photos.length === 0) return;

  const baseTimestamp = Date.now();
  const baseDBPath = `stories/${userId}/${storyId}/photos`;

  // Upload photos in parallel for better performance
  const uploadPromises = photos.map(async (photoData, index) => {
    try {
      // Convert data URL to blob for Firebase Storage upload
      const response = await fetch(photoData.url);
      const blob = await response.blob();

      // ORIGINAL APP PATHS - must match exactly
      const timestamp = baseTimestamp + index; // Ensure unique timestamps
      const filename = `img-${timestamp}.jpg`; // EXACT original app filename pattern
      const storagePath = `photos/stories/${userId}/${storyId}/${filename}`;

      // Upload to Firebase Storage using original app's uploadToStorage
      const downloadURL = await uploadToStorage(blob as File, storagePath);

      // Calculate actual aspect ratio for proper rendering
      const measuredAspect = await calculateAspectRatioFromBlob(blob);
      const aspectRatio = Math.round(((measuredAspect || 1) * 100)) / 100;

      // Create photo object matching ORIGINAL APP STRUCTURE exactly
      const newImageData = {
        url: downloadURL,
        aspectRatio, // Already rounded to 2 decimals
        dateAdded: timestamp,
        caption: '', // No caption support yet
        uri: null // Set to null after upload like original app
      };

      // CRITICAL: Update database using INDEXED PATTERN like original app
      // This creates: stories/{uid}/{storyKey}/photos/{index}
      await updateData(`${baseDBPath}/${index}`, newImageData);

      logger.info(`[feed.service] Photo ${index} uploaded to ${storagePath} and saved to ${baseDBPath}/${index}`);

    } catch (error) {
      logger.error(`Error uploading photo ${index}:`, error);
      // Don't throw - allow other photos to continue uploading
    }
  });

  // Wait for all uploads to complete
  await Promise.all(uploadPromises);
}

/**
 * Creates an exercise post - matching original app pattern
 * @param exerciseData Exercise data
 * @returns Exercise ID
 */
export const createExercisePost = async (exerciseData: any): Promise<string> => {
  const user = getCurrentUser();
  if (!user) throw new Error('Must be logged in');

  // Import the exercise service to use the proper function
  const { createExercise } = await import('./exercise.service');
  
  const exercise = {
    ...exerciseData,
    owner: user.uid,
    dateCreated: Date.now(),
    shownOnFeeds: true,
    type: exerciseData.type || 'other' // For postSubType in feed
  };

  // Use the exercise service which properly handles creation
  const createdExercise = await createExercise(user.uid, exercise);
  const exerciseId = createdExercise;
  
  logger.info('[feed.service] Exercise created, cloud function will update feeds');
  return exerciseId;
};

/**
 * Creates a story post - matching original app pattern EXACTLY
 * @param storyData Story data
 * @returns Story ID
 */
export const createStoryPost = async (storyData: any): Promise<string> => {
  const user = getCurrentUser();
  if (!user) throw new Error('Must be logged in');

  // For stories, we'll use createWithKey from api-wrapper since there's no dedicated story service
  const { createWithKey } = await import('./api-wrapper.service');
  
  // Transform Phoenix data to match original app structure EXACTLY
  const story = {
    // Required fields from original app
    timezoneOffset: new Date().getTimezoneOffset() / 60,
    title: storyData.title || '', // Title field from original
    location: storyData.location || null, // Location object from Google Places
    taggedFriends: storyData.taggedFriends || {}, // Tagged friends object
    
    // CRITICAL: message object structure from original app
    message: {
      content: storyData.story || storyData.content || '', // Map story/content to message.content
      style: storyData.style || 'standard', // 'standard' or 'bold'
      backgroundColor: storyData.backgroundColor || '', // Hex color for bold style
      textAlign: storyData.textAlign || 'center' // 'left', 'center', 'right'
    },
    
    // Photos array matching original structure (not media array)
    photos: storyData.photos || [], // Array of photo objects with url, aspectRatio, etc.
    
    // Standard fields
    dateCreated: Date.now(),
    shownOnFeeds: true
    // Note: stories use dateCreated as date, no separate date field
  };

  // Use createWithKey which properly uses push()
  const storyId = await createWithKey(`stories/${user.uid}`, story);
  
  logger.info('[feed.service] Story created with correct original app structure, cloud function will update feeds');
  return storyId;
};

/**
 * Creates a Topic post for community feed (matching original app exactly)
 * Topics go to topics/{uid}/{topicKey} and appear in public feed via onTopicChange Firebase function
 */
export const createTopicPost = async (topicData: any): Promise<string> => {
  const user = getCurrentUser();
  if (!user) throw new Error('Must be logged in');
  
  // For topics, we'll use createWithKey from api-wrapper since there's no dedicated topic service
  const { createWithKey } = await import('./api-wrapper.service');
  
  // Transform Phoenix data to match original app Topic structure EXACTLY
  const topic = {
    // Required fields from original app
    timezoneOffset: new Date().getTimezoneOffset() / 60,
    title: topicData.title || '', // Title field from original
    location: topicData.location || null, // Location object from Google Places
    taggedFriends: topicData.taggedFriends || {}, // Tagged friends object
    
    // CRITICAL: message object structure from original app (same as stories)
    message: {
      content: topicData.story || topicData.content || '', // Map story/content to message.content
      style: topicData.style || 'standard', // 'standard' or 'bold'
      backgroundColor: topicData.backgroundColor || '', // Hex color for bold style
      textAlign: topicData.textAlign || 'center' // 'left', 'center', 'right'
    },
    
    // Topics support video (Stories don't)
    video: topicData.video || null, // YouTube video object
    
    // Photos array matching original structure (not media array)
    photos: topicData.photos || [], // Array of photo objects with url, aspectRatio, etc.
    
    // Standard fields
    dateCreated: Date.now(),
    isFeatured: false // Topics can be featured
    // Note: topics use dateCreated as date, no separate date field
  };
  
  logger.info('[feed.service] Creating topic post with data:', { topicData, transformedTopic: topic });
  
  // Create topic at topics/{uid}/{topicKey} path (original app pattern)
  const topicId = await createWithKey(`topics/${user.uid}`, topic);
  
  logger.info('[feed.service] Topic created with correct original app structure, onTopicChange function will add to public feed');
  
  // Note: Firebase function onTopicChange will automatically add this to publicFeed
  return topicId;
};

/**
 * Creates a workout session post - matching original app pattern
 * @param workoutData Workout session data
 * @returns Workout session ID
 */
export const createWorkoutSessionPost = async (workoutData: any): Promise<string> => {
  const user = getCurrentUser();
  if (!user) throw new Error('Must be logged in');

  // For workout sessions, we'll use createWithKey from api-wrapper since the workout service uses set()
  const { createWithKey } = await import('./api-wrapper.service');
  
  const workout = {
    ...workoutData,
    dateCreated: Date.now(),
    shownOnFeeds: true
  };

  // Use createWithKey which properly uses push()
  const workoutId = await createWithKey(`workoutSessions/${user.uid}`, workout);
  
  logger.info('[feed.service] Workout session created, cloud function will update feeds');
  return workoutId;
};

/**
 * Toggles like on a feed item
 * Updates the actual content location based on post type
 * @param feedItemId Feed item ID
 * @param userId User ID
 * @param like True to like, false to unlike
 */
export const toggleFeedItemLike = async (
  feedItemId: string,
  userId: string,
  like: boolean
): Promise<void> => {
  try {
    // For feed likes, we need to determine the post type and location
    // This would typically come from the feed item data
    logger.warn('toggleFeedItemLike needs feed item context to determine post location');
    
    // The cloud function will handle updating counts
    logger.info('[feed.service] Like toggled, cloud function will update counts');
  } catch (error) {
    logger.error('Error toggling like:', error);
    throw error;
  }
};

/**
 * Subscribe to public feed updates
 * @param callback Callback with feed items
 * @param limit Number of items
 * @returns Unsubscribe function
 */
export const subscribeToPublicFeed = (
  callback: (items: any[]) => void,
  limit: number = 20
): (() => void) => {
  const publicFeedRef = ref(database, 'publicFeed');
  const publicFeedQuery = query(
    publicFeedRef,
    orderByChild('dateCreated'),
    limitToLast(limit)
  );

  const unsubscribe = onValue(publicFeedQuery, (snapshot) => {
    const items: any[] = [];
    
    if (snapshot.exists()) {
      snapshot.forEach((itemSnap) => {
        items.push({
          id: itemSnap.key,
          ...itemSnap.val()
        });
      });
    }

    items.reverse(); // Newest first
    callback(items);
  });

  return unsubscribe;
};

// Alias for backwards compatibility
export const toggleLikeFeedItem = toggleFeedItemLike;

/**
 * REMOVED - The original app doesn't have a generic 'post' type
 * Posts are always one of: training, competition, beltPromotion, exercise, story, workoutSession
 */

/**
 * Creates feed item from existing training
 * @param trainingId Training ID to share
 * @returns Feed item ID
 */
export const createFeedItemFromTraining = async (trainingId: string): Promise<string> => {
  const user = getCurrentUser();
  if (!user) throw new Error('Must be logged in');

  // Get the training data
  const trainingRef = ref(database, `trainings/${trainingId}`);
  const trainingSnapshot = await get(trainingRef);
  
  if (!trainingSnapshot.exists()) {
    throw new Error('Training not found');
  }

  
  // Update training to show on feeds using api-wrapper which ensures database is online
  await updateData(`trainings/${trainingId}`, {
    shownOnFeed: true,
    shownOnFeeds: true,
    dateModified: Date.now()
  });

  // Cloud function will handle adding to feeds
  return trainingId;
};

/**
 * Gets comments for a post (delegates to comments service)
 * @param postKey Post key
 * @returns Comments array
 */
export const getCommentsForPost = async (postKey: string): Promise<any[]> => {
  // Import dynamically to avoid circular dependency
  const { getComments } = await import('./comments.service');
  return getComments(postKey);
};

/**
 * Adds a comment to a post (delegates to comments service)
 * @param postKey Post key
 * @param text Comment text
 * @returns Comment ID
 */
export const addComment = async (postKey: string, text: string): Promise<string> => {
  // Import dynamically to avoid circular dependency
  const { addComment: addCommentToPost } = await import('./comments.service');
  return addCommentToPost(postKey, text);
};

/**
 * Updates a topic or story post
 * @param postId Post ID
 * @param postType Post type ('topic' or 'story')
 * @param ownerId Owner user ID
 * @param updates Updated fields
 */
export const updatePost = async (postId: string, postType: string, ownerId: string, updates: any): Promise<void> => {
  const { updateData } = await import('./api-wrapper.service');
  const path = postType === 'topic' ? `topics/${ownerId}/${postId}` : `stories/${ownerId}/${postId}`;
  await updateData(path, updates);
};

/**
 * Deletes a topic or story post
 * @param postId Post ID
 * @param postType Post type ('topic' or 'story')
 * @param ownerId Owner user ID
 */
export const deletePost = async (postId: string, postType: string, ownerId: string): Promise<void> => {
  const { removeData, getData } = await import('./api-wrapper.service');

  // Delete from the main storage location
  const path = postType === 'topic' ? `topics/${ownerId}/${postId}` : `stories/${ownerId}/${postId}`;
  logger.info(`[feed.service] Deleting post from ${path}`);

  // Check if the post exists at the expected location
  try {
    const postData = await getData(path);
    if (postData) {
      await removeData(path);
      logger.info(`[feed.service] Successfully deleted post from ${path}`);
    } else {
      logger.warn(`[feed.service] Post not found at ${path}, may have been already deleted`);
    }
  } catch (error) {
    logger.error(`[feed.service] Error deleting post from ${path}:`, error);
    // Continue to try deleting from publicFeed even if main delete fails
  }

  // If it's a topic (public post), also remove from publicFeed index
  if (postType === 'topic') {
    try {
      logger.info(`[feed.service] Removing topic from publicFeed/${postId}`);
      await removeData(`publicFeed/${postId}`);
      logger.info(`[feed.service] Successfully removed from publicFeed`);
    } catch (error) {
      logger.error(`[feed.service] Error removing from publicFeed:`, error);
    }
  }

  // Note: Personal feed entries (feeds/{uid}/{postId}) are managed by Firebase Cloud Functions
  // They will be automatically removed when the original post is deleted
};

/**
 * Deletes a comment (delegates to comments service)
 * @param postKey Post key
 * @param commentId Comment ID
 */
export const deleteComment = async (postKey: string, commentId: string): Promise<void> => {
  // Import dynamically to avoid circular dependency
  const { deleteComment: deletePostComment } = await import('./comments.service');
  return deletePostComment(postKey, commentId);
};
